import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@geely/ui";
import { Clock, List, Grid3x3 } from 'lucide-react';
import { getStatusLabel, getStatusColor, formatTime } from '@/lib/utils';
import { AppointmentDetailDialog } from './AppointmentDetailDialog';
import { ALL_DATES_VALUE } from './DateFilter';
import type { Appointment } from '@geely/shared';

interface StoreAgendaProps {
  timeSlots: string[];
  appointments: Appointment[];
  maxParallelAppointments: number;
  selectedDate?: string;
}

interface TimeSlotCardProps {
  time: string;
  appointments: Appointment[];
  maxParallel: number;
  onAppointmentClick: (apt: Appointment) => void;
}

function TimeSlotCard({ time, appointments, maxParallel, onAppointmentClick }: TimeSlotCardProps) {
  const isFull = appointments.length >= maxParallel;
  const hasAppointments = appointments.length > 0;
  const MAX_DISPLAYED_APPOINTMENTS = 5;
  const displayedAppointments = appointments.slice(0, MAX_DISPLAYED_APPOINTMENTS);
  const hiddenCount = Math.max(0, appointments.length - MAX_DISPLAYED_APPOINTMENTS);

  return (
    <div
      className={`border rounded-lg p-3 transition-all ${
        hasAppointments
          ? isFull
            ? 'bg-red-50 border-red-200'
            : 'bg-blue-50 border-blue-200'
          : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1">
          <Clock className="size-3 text-gray-600" />
          <span className="font-semibold text-sm">{formatTime(time)}</span>
        </div>
        <span className="text-xs text-gray-600 bg-white px-2 py-0.5 rounded">
          {appointments.length}/{maxParallel}
        </span>
      </div>

      {appointments.length > 0 ? (
        <div className="space-y-1.5">
          {displayedAppointments.map((apt) => (
            <div
              key={apt.id}
              className="bg-white border border-gray-200 rounded p-2 text-xs cursor-pointer hover:shadow-sm hover:border-blue-300 transition-all"
              onClick={() => onAppointmentClick(apt)}
            >
              <div className="flex items-start justify-between gap-1 mb-1">
                <span className="font-medium text-xs truncate">
                  {apt.customerName}
                </span>
                <Badge
                  variant="outline"
                  className={`text-[10px] px-1 py-0 ${getStatusColor(apt.status)}`}
                >
                  {getStatusLabel(apt.status).substring(0, 4)}
                </Badge>
              </div>
              <div className="text-gray-600 text-[10px]">
                <p className="truncate">{apt.customerPhone}</p>
              </div>
            </div>
          ))}
          {hiddenCount > 0 && (
            <div className="text-xs text-gray-500 italic text-center py-1 bg-gray-100 rounded">
              + {hiddenCount} agendamento{hiddenCount > 1 ? 's' : ''} não exibido{hiddenCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-gray-400 italic text-center py-2">
          Livre
        </div>
      )}
    </div>
  );
}

interface TimeSlotListItemProps {
  time: string;
  appointments: Appointment[];
  maxParallel: number;
  onAppointmentClick: (apt: Appointment) => void;
}

function TimeSlotListItem({ time, appointments, maxParallel, onAppointmentClick }: TimeSlotListItemProps) {
  const MAX_DISPLAYED_APPOINTMENTS = 5;
  const displayedAppointments = appointments.slice(0, MAX_DISPLAYED_APPOINTMENTS);
  const hiddenCount = Math.max(0, appointments.length - MAX_DISPLAYED_APPOINTMENTS);

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock className="size-4 text-gray-500" />
          <span className="font-medium">{formatTime(time)}</span>
        </div>
        <span className="text-xs text-gray-500">
          {appointments.length} / {maxParallel} vagas
        </span>
      </div>

      {appointments.length > 0 ? (
        <div className="space-y-2">
          {displayedAppointments.map((apt) => (
            <div
              key={apt.id}
              className="bg-white border border-gray-200 rounded p-3 text-sm cursor-pointer hover:shadow-sm hover:border-blue-300 transition-all"
              onClick={() => onAppointmentClick(apt)}
            >
              <div className="flex items-start justify-between mb-1">
                <span className="font-medium">{apt.customerName}</span>
                <Badge
                  variant="outline"
                  className={getStatusColor(apt.status)}
                >
                  {getStatusLabel(apt.status)}
                </Badge>
              </div>
              <div className="text-gray-600 text-xs space-y-1">
                <p>Tel: {apt.customerPhone}</p>
                <p>ID: {apt.id}</p>
              </div>
            </div>
          ))}
          {hiddenCount > 0 && (
            <div className="text-sm text-gray-500 italic text-center py-2 bg-gray-100 rounded">
              + {hiddenCount} agendamento{hiddenCount > 1 ? 's' : ''} não exibido{hiddenCount > 1 ? 's' : ''}
            </div>
          )}
        </div>
      ) : (
        <div className="text-sm text-gray-400 italic">
          Nenhum agendamento
        </div>
      )}
    </div>
  );
}

export function StoreAgenda({ timeSlots, appointments, maxParallelAppointments, selectedDate }: StoreAgendaProps) {
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleAppointmentClick = (apt: Appointment) => {
    setSelectedAppointment(apt);
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setSelectedAppointment(null);
  };

  const getSlotAppointments = (time: string) =>
    appointments.filter((apt) => apt.time === time);

  if (timeSlots.length === 0) {
    const isAllDatesSelected = selectedDate === ALL_DATES_VALUE;

    return (
      <Card>
        <CardHeader>
          <CardTitle>Agenda do Dia</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            {isAllDatesSelected
              ? 'Selecione uma data específica para visualizar a agenda'
              : 'Loja fechada neste dia'}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agenda do Dia</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="calendar" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2">
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <Grid3x3 className="size-4" />
              <span>Calendário</span>
            </TabsTrigger>
            <TabsTrigger value="list" className="flex items-center gap-2">
              <List className="size-4" />
              <span>Lista</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 max-h-[600px] overflow-y-auto">
              {timeSlots.map((time) => (
                <TimeSlotCard
                  key={time}
                  time={time}
                  appointments={getSlotAppointments(time)}
                  maxParallel={maxParallelAppointments}
                  onAppointmentClick={handleAppointmentClick}
                />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="list" className="mt-4">
            <div className="space-y-2">
              {timeSlots.map((time) => (
                <TimeSlotListItem
                  key={time}
                  time={time}
                  appointments={getSlotAppointments(time)}
                  maxParallel={maxParallelAppointments}
                  onAppointmentClick={handleAppointmentClick}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>

      <AppointmentDetailDialog
        appointment={selectedAppointment}
        isOpen={isDialogOpen}
        onClose={handleDialogClose}
      />
    </Card>
  );
}
